import { Card, formatDate, Heading, Pagination, Text } from '@amelio/ui-library';

import type { User } from '@/types/directory/User';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { PastIllustration } from '@/components/Illustrations/Illustration';
import { ProfileCard } from '@/components/ProfileCard/ProfileCard';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { ReviewType } from '@/constants/review/CycleInstance';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';
import { getReviewTypeIcon, getReviewTypeLabel, hasRespondents } from '@/utils/cycleInstance';
import { getAllReviewers, getCurrentUser } from '@/utils/reviewInstance';

const PAGE_SIZE = 6;

interface PastReviewsProps {
    pastReviews: ReviewInstance[];
    isLoading: boolean;
    currentPage: number;
    totalCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    users: User[];
    profileNameByUserId?: Record<string, string>;
    onViewResults: (reviewInstance: ReviewInstance) => void;
    hasActiveFilters?: boolean;
}

export const PastReviews = ({
    pastReviews,
    isLoading,
    currentPage,
    totalCount,
    totalPages,
    onPageChange,
    users,
    profileNameByUserId = {},
    onViewResults,
    hasActiveFilters,
}: PastReviewsProps) => {
    const { t } = useAmelioTranslations();

    // A tile dates an anonymous 360 by nothing a respondent did. One that expired closed on its due date, which every
    // reader knows, and is said so in the words of its results; one completed by its last respondent is said to be done.
    const endDateLabel = (review: ReviewInstance): string | undefined => {
        const isAnonymous = isAnonymousFeedback(review.feedbackPrivacy);
        if (isAnonymous && review.status !== ReviewInstanceStatus.EXPIRED) {
            return t('evaluation360Dashboard.pastReviews.completed');
        }

        const [key, date] = isAnonymous
            ? (['results.belowThreshold.closedOn', review.cycleInstance?.dueDate] as const)
            : (['evaluation360Dashboard.pastReviews.completedOn', review.completedAt] as const);

        return date ? t(key, { date: formatDate({ date, language: i18n.language }) }) : undefined;
    };

    // Only when there is nothing to show: swapping a populated grid for bones collapses the
    // card height mid-fetch, and the browser clamps the page scroll.
    const showSkeleton = isLoading && pastReviews.length === 0;

    return (
        <Card
            className={
                'gradient-border-pastel-warm mt-40 !px-4 !py-2 sm:mt-0 md:-mt-16 md:!px-8 lg:!px-15 lg:!py-4'
            }
            padding={'none'}
            variant={'flat'}
            borderRadius={'xl'}
        >
            <PastIllustration className={'relative -top-40 ml-auto block h-56 w-56'} />

            <div className={'-mt-48 mb-6 md:w-1/2'}>
                <Heading level={3}>{t('evaluation360Dashboard.pastReviews.title')}</Heading>

                <Text className={'mt-2'}>
                    {t('evaluation360Dashboard.pastReviews.description')}
                </Text>
            </div>

            {showSkeleton ? (
                <Skeleton className={'flex flex-row gap-4'}>
                    <Bone className={'h-80 flex-1 rounded-2xl'} />

                    <Bone className={'h-80 flex-1 rounded-2xl'} />

                    <Bone className={'h-80 flex-1 rounded-2xl'} />
                </Skeleton>
            ) : (
                <>
                    {pastReviews.length === 0 && hasActiveFilters && (
                        <Text className={'py-6 text-center'}>
                            {t('evaluation360Dashboard.noFilterResults')}
                        </Text>
                    )}

                    <div className={'grid !grid-cols-1 gap-4 pt-2 sm:!grid-cols-2 lg:!grid-cols-3'}>
                        {pastReviews.length > 0 &&
                            pastReviews.map((review) => {
                                const revieweeId =
                                    review.reviewAudienceAssignment?.assignedTo ??
                                    review.assignedTo;
                                const endedOn = endDateLabel(review);

                                return (
                                    <ProfileCard
                                        key={review.id}
                                        infoBadges={[
                                            {
                                                icon: 'business-chart',
                                                id: `${review.id}-badge-profile`,
                                                primaryLabel:
                                                    profileNameByUserId[revieweeId] ??
                                                    t(
                                                        'evaluation360Dashboard.reviewsToBeLaunched.revieweeBadge.noAssignedProfile',
                                                    ),
                                                secondaryLabel: t('common.profile'),
                                            },
                                            {
                                                icon: getReviewTypeIcon(
                                                    review?.cycleInstance?.reviewType ??
                                                        ReviewType.EVALUATION_360,
                                                ),
                                                id: `${review.id}-evaluation-type`,
                                                primaryLabel: getReviewTypeLabel(
                                                    review.cycleInstance?.reviewType ??
                                                        ReviewType.EVALUATION_360,
                                                ),
                                                secondaryLabel: endedOn,
                                            },
                                        ]}
                                        reviewers={
                                            hasRespondents(review.cycleInstance?.reviewType)
                                                ? getAllReviewers({ reviewInstance: review, users })
                                                : undefined
                                        }
                                        user={getCurrentUser({ reviewInstance: review, users })}
                                        onViewResults={onViewResults}
                                        reviewInstance={review}
                                    />
                                );
                            })}
                    </div>
                </>
            )}

            <Pagination
                variant={'rounded'}
                showResultsSummary={true}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={onPageChange}
                isLoading={isLoading}
                dataTestId={'past-reviews-pagination'}
                resultsSummaryText={t('common.showingResults', {
                    start: totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
                    end: Math.min(currentPage * PAGE_SIZE, totalCount),
                    total: totalCount,
                })}
            />
        </Card>
    );
};
